/**
 * Lightweight client-side text sanitization for free-text fields and
 * AI-generated copy (service name, tagline, description, add-on names, etc.).
 *
 * React already escapes text on render, so this is defense-in-depth for any
 * copy that may later be rendered as HTML downstream (emails, tickets, the
 * customer widget) and to keep stored data clean. The backend also sanitizes.
 */

/** Strip HTML tags and collapse dangerous control characters from a string. */
export function stripHtml(input) {
  if (input == null) return '';
  return String(input)
    // remove <script>…</script> and <style>…</style> blocks entirely
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
    // remove any remaining tags
    .replace(/<\/?[^>]+(>|$)/g, '')
    // strip control chars (keep \n and \t)
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
}

/**
 * Sanitize a free-text value: strip HTML, normalize whitespace, optionally cap
 * length. Returns a clean single/multi-line string ready to store.
 *
 * @param {string} input
 * @param {{ maxLength?: number, singleLine?: boolean }} [opts]
 */
export function sanitizeText(input, opts = {}) {
  const { maxLength, singleLine = false } = opts;
  let out = stripHtml(input);
  if (singleLine) {
    out = out.replace(/\s+/g, ' ');
  } else {
    // collapse runs of spaces/tabs but preserve newlines
    out = out.replace(/[^\S\n]+/g, ' ').replace(/\n{3,}/g, '\n\n');
  }
  out = out.replace(/^\s+/, '').replace(/\s+$/, '');
  if (typeof maxLength === 'number' && out.length > maxLength) {
    out = out.slice(0, maxLength);
  }
  return out;
}

export default sanitizeText;
